// Public-flow address autocomplete composable (provider-agnostic)
import type { AutocompleteInstance, ProviderAddressResponse } from '~/lib/geo-providers/interfaces'
import { resolveProvider } from '~/lib/geo-providers/resolveProvider'
import { getProviderConfig } from './getProviderConfig'
import { useLogger } from '~/composables/useLogger'
import type { AddressSelection } from './AddressSelection'

// --- Composable ---

export const useAddressAutocomplete = () => {
  const logger = useLogger('useAddressAutocomplete')
  const { provider: config } = getProviderConfig()

  const createInstance = async (
    containerId: string,
    placeholder: string,
    theme: 'light' | 'dark'
  ): Promise<AutocompleteInstance | null> => {
    try {
      const provider = await resolveProvider()
      return await provider.create(containerId, placeholder, {
        name: config.name,
        accessToken: config.accessToken,
        logger,
        theme
      })
    } catch (error) {
      logger.error('initAutocomplete', error)
      return null
    }
  }

  const initAutocomplete = async (
    containerId: string,
    onSelect: (_selection: AddressSelection) => void | Promise<void>,
    placeholder = 'Enter location',
    theme: 'light' | 'dark' = 'light'
  ): Promise<AutocompleteInstance | null> => {
    const instance = await createInstance(containerId, placeholder, theme)
    if (!instance) return null

    instance.addEventListener('retrieve', (e: CustomEvent<ProviderAddressResponse>) => {
      const { address, lat, lng, bbox, place_type } = e.detail
      if (lat && lng) {
        Promise.resolve(onSelect({ address, lat, lng, bbox, place_type })).catch(
          (error: unknown) => {
            logger.error('initAutocomplete', error)
          }
        )
      }
    })

    return instance
  }

  return {
    initAutocomplete
  }
}
